import React, { useRef } from 'react';
import { X, Printer, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

export const PrintSummaryModal = ({
  isOpen,
  onClose,
  records,
  paymentMethodTotals,
  userTotals,
  filters,
  stats
}) => {
  const { t } = useTranslation();
  const printRef = useRef(null);

  if (!isOpen) return null;

  // Transform paymentMethodTotals object to array if needed
  const paymentMethodTotalsArray = paymentMethodTotals && typeof paymentMethodTotals === 'object' && !Array.isArray(paymentMethodTotals)
    ? Object.entries(paymentMethodTotals).map(([method, data]) => ({
        method,
        ...data,
        percentage: stats.totalCollected > 0 ? (data.deposits / stats.totalCollected) * 100 : 0
      }))
    : paymentMethodTotals || [];

  // Transform userTotals object to array if needed
  const userTotalsArray = userTotals && typeof userTotals === 'object' && !Array.isArray(userTotals)
    ? Object.entries(userTotals).map(([userName, data]) => ({
        userName,
        totalTransactions: data.count,
        netAmount: data.total,
        depositPercentage: stats.totalCollected > 0 ? (data.deposits / stats.totalCollected) * 100 : 0,
        ...data
      })).sort((a, b) => b.netAmount - a.netAmount)
    : userTotals || [];

  const getActiveFiltersText = () => {
    const activeFilters = [];
    if (filters.searchTerm) activeFilters.push(`${t('search')}: ${filters.searchTerm}`);
    if (filters.userFilter) activeFilters.push(`${t('user')}: ${filters.userFilter}`);
    if (filters.dateFrom && filters.dateTo) {
      activeFilters.push(`${t('period')}: ${filters.dateFrom} ${t('to')} ${filters.dateTo}`);
    } else if (filters.activeQuickFilter) {
      const quickFilterLabels = {
        'week': t('last7Days'),
        'lastMonth': t('lastMonth'),
        'thisMonth': t('thisMonth')
      };
      activeFilters.push(`${t('period')}: ${quickFilterLabels[filters.activeQuickFilter] || filters.activeQuickFilter}`);
    }
    if (filters.requestIdFilter) activeFilters.push(`${t('requestId')}: ${filters.requestIdFilter}`);
    if (filters.recordTypeFilter !== 'all') {
      const typeLabels = {
        'deposit': t('depositsOnly'),
        'refund': t('refundsOnly')
      };
      activeFilters.push(`${t('type')}: ${typeLabels[filters.recordTypeFilter]}`);
    }
    return activeFilters.length > 0 ? activeFilters.join(', ') : t('noFiltersApplied');
  };

  const handlePrint = () => {
    if (printRef.current) {
      // Create print styles
      const printStyles = document.createElement('style');
      printStyles.innerHTML = `
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap');
        
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            font-family: 'Cairo', 'Arial', sans-serif;
            background: white !important;
            color: black !important;
            line-height: 1.3;
            padding: 10px;
            box-sizing: border-box;
            font-size: 12px;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-family: 'Cairo', 'Arial', sans-serif !important;
          }
          .bg-white { background: white !important; }
          .text-gray-900 { color: #111827 !important; }
          .text-gray-800 { color: #1f2937 !important; }
          .text-gray-700 { color: #374151 !important; }
          .text-gray-600 { color: #4b5563 !important; }
          .text-gray-500 { color: #6b7280 !important; }
          .text-slate-900 { color: #0f172a !important; }
          .text-slate-800 { color: #1e293b !important; }
          .text-slate-700 { color: #334155 !important; }
          .text-slate-600 { color: #475569 !important; }
          .text-slate-500 { color: #64748b !important; }
          .border-gray-300 { border-color: #d1d5db !important; }
          .border-gray-200 { border-color: #e5e7eb !important; }
          .border-slate-200 { border-color: #e2e8f0 !important; }
          .border-t { border-top-width: 1px !important; border-top-style: solid !important; }
          .border-b { border-bottom-width: 1px !important; border-bottom-style: solid !important; }
          .border-t-2 { border-top-width: 1px !important; border-top-style: solid !important; }
          .border-b-2 { border-bottom-width: 1px !important; border-bottom-style: solid !important; }
          .font-bold { font-weight: 700 !important; }
          .font-semibold { font-weight: 600 !important; }
          .font-medium { font-weight: 500 !important; }
          .text-sm { font-size: 10px !important; }
          .text-xs { font-size: 9px !important; }
          .text-lg { font-size: 14px !important; }
          .text-xl { font-size: 16px !important; }
          .text-2xl { font-size: 18px !important; }
          .text-3xl { font-size: 20px !important; }
          .mb-1 { margin-bottom: 3px !important; }
          .mb-2 { margin-bottom: 5px !important; }
          .mb-3 { margin-bottom: 7px !important; }
          .mb-4 { margin-bottom: 9px !important; }
          .mb-6 { margin-bottom: 11px !important; }
          .mb-8 { margin-bottom: 13px !important; }
          .mt-1 { margin-top: 3px !important; }
          .mt-2 { margin-top: 5px !important; }
          .mt-4 { margin-top: 9px !important; }
          .mt-6 { margin-top: 11px !important; }
          .p-2 { padding: 6px !important; }
          .p-3 { padding: 8px !important; }
          .p-4 { padding: 10px !important; }
          .p-6 { padding: 11px !important; }
          .p-8 { padding: 10px !important; }
          .px-3 { padding-left: 8px !important; padding-right: 8px !important; }
          .px-4 { padding-left: 10px !important; padding-right: 10px !important; }
          .py-1 { padding-top: 3px !important; padding-bottom: 3px !important; }
          .py-2 { padding-top: 5px !important; padding-bottom: 5px !important; }
          .pt-2 { padding-top: 5px !important; }
          .pt-4 { padding-top: 9px !important; }
          .pt-6 { padding-top: 11px !important; }
          .pb-2 { padding-bottom: 5px !important; }
          .pb-4 { padding-bottom: 9px !important; }
          .text-center { text-align: center !important; }
          .text-right { text-align: right !important; }
          .flex { display: flex !important; }
          .justify-between { justify-content: space-between !important; }
          .justify-center { justify-content: center !important; }
          .items-center { align-items: center !important; }
          .space-y-1 > * + * { margin-top: 3px !important; }
          .space-y-2 > * + * { margin-top: 4px !important; }
          .space-y-3 > * + * { margin-top: 5px !important; }
          .space-y-4 > * + * { margin-top: 7px !important; }
          .space-y-6 > * + * { margin-top: 9px !important; }
          .max-w-4xl { max-width: 100% !important; }
          .mx-auto { margin-left: auto !important; margin-right: auto !important; }
          .rounded-lg { border-radius: 4px !important; }
          .rounded-xl { border-radius: 5px !important; }
          .bg-slate-50 { background-color: #f8fafc !important; }
          .bg-green-50 { background-color: #f0fdf4 !important; }
          .bg-blue-50 { background-color: #eff6ff !important; }
          .bg-red-50 { background-color: #fef2f2 !important; }
          .text-green-600 { color: #16a34a !important; }
          .text-blue-600 { color: #2563eb !important; }
          .text-red-600 { color: #dc2626 !important; }
          .text-green-700 { color: #15803d !important; }
          .text-blue-700 { color: #1d4ed8 !important; }
          .text-red-700 { color: #b91c1c !important; }
          .text-slate-700 { color: #334155 !important; }
          .grid { display: grid !important; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .grid-cols-2 { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; }
          .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; }
          .gap-4 { gap: 7px !important; }
          .gap-6 { gap: 9px !important; }
          .gap-8 { gap: 11px !important; }
          .w-8 { width: 20px !important; }
          .h-8 { height: 20px !important; }
          .w-6 { width: 18px !important; }
          .h-6 { height: 18px !important; }
          .rounded-full { border-radius: 50% !important; }
          .bg-yellow-500 { background-color: #eab308 !important; }
          .bg-blue-500 { background-color: #3b82f6 !important; }
          .bg-slate-400 { background-color: #94a3b8 !important; }
          .text-white { color: white !important; }
          
          /* Single page constraints */
          .print-content {
            max-height: 100vh !important;
            overflow: hidden !important;
          }
          
          /* Limit agent performance to 4 items for more space */
          .space-y-3 > *:nth-child(n+5) {
            display: none !important;
          }
          .space-y-2 > *:nth-child(n+5) {
            display: none !important;
          }
        }
        @page {
          margin: 0.3in;
          size: A4 portrait;
        }
      `;
      
      // Add print styles to head
      document.head.appendChild(printStyles);
      
      // Clone and prepare content for printing
      const printContent = printRef.current.cloneNode(true);
      printContent.classList.add('print-content');
      
      // Remove any existing print content
      const existingPrintContent = document.querySelector('.print-content');
      if (existingPrintContent) {
        existingPrintContent.remove();
      }
      
      // Add the print content to body
      document.body.appendChild(printContent);
      
      // Set document title for print
      const originalTitle = document.title;
      document.title = 'Deposit Collection Summary Report';
      
      // Focus on window and print
      window.focus();
      
      // Use setTimeout to ensure content is rendered before printing
      setTimeout(() => {
        window.print();
        
        // Listen for after print event to clean up
        const afterPrint = () => {
          // Clean up
          if (document.head.contains(printStyles)) {
            document.head.removeChild(printStyles);
          }
          if (document.body.contains(printContent)) {
            document.body.removeChild(printContent);
          }
          document.title = originalTitle;
          
          // Remove event listeners
          window.removeEventListener('afterprint', afterPrint);
        };
        
        // Add event listener for cleanup
        window.addEventListener('afterprint', afterPrint);
        
        // Fallback cleanup after 3 seconds
        setTimeout(afterPrint, 3000);
      }, 100);
    }
  };

  const handleDownload = () => {
    if (printRef.current) {
      // Disable scroll during PDF generation
      document.body.style.overflow = 'hidden';
      
      // Show loading state
      const loadingDiv = document.createElement('div');
      loadingDiv.innerHTML = 'Generating PDF...';
      loadingDiv.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); z-index: 9999;';
      document.body.appendChild(loadingDiv);

      // Clone the element and apply single-page styles
      const elementToCapture = printRef.current.cloneNode(true);
      
      // Apply styles for single-page PDF rendering with matching print padding
      elementToCapture.style.width = '794px'; // A4 width in pixels (210mm)
      elementToCapture.style.maxHeight = '1123px'; // A4 height in pixels (297mm)
      elementToCapture.style.padding = '10px'; // Match print padding
      elementToCapture.style.backgroundColor = 'white';
      elementToCapture.style.fontFamily = "'Cairo', 'Arial', sans-serif";
      elementToCapture.style.fontSize = '12px'; // Bigger font
      elementToCapture.style.lineHeight = '1.3'; // More spacing
      elementToCapture.style.overflow = 'hidden';
      elementToCapture.style.boxSizing = 'border-box';
      
      // Match print spacing exactly with optimized margins for bigger font
      const sections = elementToCapture.querySelectorAll('.mb-8');
      sections.forEach(section => {
        section.style.marginBottom = '13px'; // Match print .mb-8
      });
      
      const mb6Sections = elementToCapture.querySelectorAll('.mb-6');
      mb6Sections.forEach(section => {
        section.style.marginBottom = '11px'; // Match print .mb-6
      });
      
      const mb4Sections = elementToCapture.querySelectorAll('.mb-4');
      mb4Sections.forEach(section => {
        section.style.marginBottom = '9px'; // Match print .mb-4
      });
      
      const headers = elementToCapture.querySelectorAll('.text-2xl');
      headers.forEach(header => {
        header.style.fontSize = '18px'; // Bigger font
        header.style.marginBottom = '5px';
      });
      
      const subHeaders = elementToCapture.querySelectorAll('.text-sm');
      subHeaders.forEach(header => {
        header.style.fontSize = '10px'; // Bigger font
        header.style.marginBottom = '7px';
      });
      
      // Match print padding for containers exactly
      const paddedElements = elementToCapture.querySelectorAll('.p-3, .p-2, .p-8');
      paddedElements.forEach(element => {
        if (element.classList.contains('p-8')) {
          element.style.padding = '10px'; // Match print .p-8
        } else if (element.classList.contains('p-3')) {
          element.style.padding = '8px'; // Match print .p-3
        } else if (element.classList.contains('p-2')) {
          element.style.padding = '6px'; // Match print .p-2
        }
      });
      
      // Apply gap spacing to match print
      const gridElements = elementToCapture.querySelectorAll('.gap-3, .gap-4');
      gridElements.forEach(element => {
        if (element.classList.contains('gap-3')) {
          element.style.gap = '7px'; // Match print
        }
      });
      
      // Apply space-y spacing to match print
      const spaceYElements = elementToCapture.querySelectorAll('.space-y-2');
      spaceYElements.forEach(element => {
        const children = element.children;
        for (let i = 1; i < children.length; i++) {
          children[i].style.marginTop = '4px'; // Match print .space-y-2
        }
      });
      
      // Limit agent performance to 4 items to match print
      const agentItems = elementToCapture.querySelectorAll('.space-y-2 > *');
      agentItems.forEach((item, index) => {
        if (index >= 4) {
          item.style.display = 'none';
        }
      });
      
      // Match print font sizes - bigger
      const textElements = elementToCapture.querySelectorAll('.text-xs, .text-lg');
      textElements.forEach(element => {
        if (element.classList.contains('text-xs')) {
          element.style.fontSize = '9px'; // Bigger from 8px
        } else if (element.classList.contains('text-lg')) {
          element.style.fontSize = '14px'; // Bigger from 13px
        }
      });
      
      // Temporarily add to DOM for accurate rendering
      elementToCapture.style.position = 'absolute';
      elementToCapture.style.left = '-9999px';
      elementToCapture.style.top = '0';
      document.body.appendChild(elementToCapture);

      html2canvas(elementToCapture, {
        scale: 3, // Reduced scale for better single-page fit
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123,
        scrollX: 0,
        scrollY: 0
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        const imgWidth = 210; // A4 portrait width in mm
        const pageHeight = 297; // A4 portrait height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Ensure content fits on one page
        if (imgHeight > pageHeight) {
          const scaleFactor = pageHeight / imgHeight;
          const scaledWidth = imgWidth * scaleFactor;
          const scaledHeight = pageHeight;
          const xOffset = (imgWidth - scaledWidth) / 2;
          
          pdf.addImage(imgData, 'PNG', xOffset, 0, scaledWidth, scaledHeight);
        } else {
          pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        }
        
        // Save the PDF
        const fileName = `deposit-collection-summary-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
        
        // Clean up
        if (document.body.contains(elementToCapture)) {
          document.body.removeChild(elementToCapture);
        }
        if (document.body.contains(loadingDiv)) {
          document.body.removeChild(loadingDiv);
        }
        // Re-enable scroll
        document.body.style.overflow = '';
        
      }).catch(error => {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
        
        // Clean up on error
        if (document.body.contains(elementToCapture)) {
          document.body.removeChild(elementToCapture);
        }
        if (document.body.contains(loadingDiv)) {
          document.body.removeChild(loadingDiv);
        }
        // Re-enable scroll
        document.body.style.overflow = '';
      });
    }
  };

  // Helper to format YYYY-MM-DD or YYYY/MM/DD to DD-MM-YYYY
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-GB').replace(/\//g, '-');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                {t('printSummaryReport')}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t('summaryForPeriod')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Print Content */}
        <div className="p-6">
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <div ref={printRef} className="bg-white p-8 max-w-4xl mx-auto">
              {/* Report Header */}
              <div className="text-center mb-6 border-b-2 border-gray-300 pb-4">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('depositCollectionSummary')}
                </h1>
                <p className="text-xs text-gray-600 mb-2">
                  {t('generatedOn')} {new Date().toLocaleDateString('en-GB')} {t('at')} {new Date().toLocaleTimeString('en-GB')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('confidentialReport')}
                </p>
              </div>

              {/* Report Criteria */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('reportCriteria')}:
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1 text-xs">{t('reportPeriod')}:</h4>
                    <p className="text-xs text-gray-600">
                      <span>{t("period")}: </span>
                      {filters.dateFrom && filters.dateTo 
                        ? `${formatDateDMY(filters.dateFrom)} ${t('to')} ${formatDateDMY(filters.dateTo)}`
                        : filters.activeQuickFilter
                          ? (() => {
                              const quickFilterLabels = {
                                'last7Days': t('last7Days'),
                                'lastMonth': t('lastMonth'),
                                'thisMonth': t('thisMonth')
                              };
                              return quickFilterLabels[filters.activeQuickFilter];
                            })()
                          : t('allTime')
                      }
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1 text-xs">{t('appliedFilters')}:</h4>
                    <p className="text-xs text-gray-600">
                      {getActiveFiltersText()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('financialSummary')}:
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-blue-700 mb-1">
                      {stats.totalCollected.toLocaleString()}
                    </div>
                    <div className="text-xs text-blue-600">{t('EGP')} {t('deposit')}</div>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-red-700 mb-1">
                      {stats.totalRefunds.toLocaleString()}
                    </div>
                    <div className="text-xs text-red-600">{t('EGP')} {t('refunds')}</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-green-700 mb-1">
                      {stats.netAmount.toLocaleString()}
                    </div>
                    <div className="text-xs text-green-600">{t('EGP')} {t('netAmount')}</div>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-slate-700 mb-1">
                      {stats.totalRecords}
                    </div>
                    <div className="text-xs text-slate-600">{t('totalRecords')}</div>
                  </div>
                </div>
              </div>

              {/* Payment Method Breakdown */}
              {paymentMethodTotalsArray.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                    {t('paymentMethodBreakdown')}:
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {paymentMethodTotalsArray.slice(0, 4).map((method) => (
                      <div key={method.method} className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-gray-800 text-xs">{t(method.method) || method.method}</span>
                          <span className="text-sm font-bold text-gray-900">
                            {(method.total || 0).toLocaleString()} {t('EGP')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="flex justify-between">
                            <span>{t('recordCount')}:</span>
                            <span>{method.count || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>{t('percentage')}:</span>
                            <span>{(method.percentage || 0).toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Performance Summary */}
              {userTotalsArray.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                    {t('agentPerformanceSummary')} (Top 4):
                  </h3>
                  <div className="space-y-2">
                    {userTotalsArray.slice(0, 4).map((user, index) => (
                      <div key={user.userName} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                            index === 0 ? 'bg-yellow-500' : index < 3 ? 'bg-blue-500' : 'bg-slate-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-xs">{user.userName}</div>
                            <div className="text-xs text-gray-600">
                              {user.totalTransactions} {t('transactions')}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-gray-900 text-xs">
                            {(user.netAmount || 0).toLocaleString()} {t('EGP')}
                          </div>
                          <div className="text-xs text-gray-600">
                            {(user.depositPercentage || 0).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary Statistics */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {t('summaryStatistics')}:
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                  <div>
                    <div className="text-sm font-bold text-gray-900">{stats.totalRecords || 0}</div>
                    <div className="text-xs text-gray-600">{t('totalRecords')}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{stats.activeAgents || 0}</div>
                    <div className="text-xs text-gray-600">{t('totalAgents')}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{stats.totalRecords || 0}</div>
                    <div className="text-xs text-gray-600">{t('totalTransactions')}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      {(stats.averageDeposit || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">{t('averageDeposit')} {t('EGP')}</div>
                  </div>
                </div>
              </div>

              {/* Report Footer */}
              <div className="text-center text-xs text-gray-500 border-t border-gray-300 pt-3">
                <p className="mb-1">{t('reportGenerated')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <p className="font-medium text-xs">{t('contactInfo')}:</p>
                    <p className="text-xs">info@egarhub.com</p>
                  </div>
                  <div>
                    <p className="font-medium text-xs">{t('reportPeriod')}:</p>
                    <p className="text-xs">
                      {filters.dateFrom && filters.dateTo 
                        ? `${formatDateDMY(filters.dateFrom)} - ${formatDateDMY(filters.dateTo)}`
                        : t('allAvailableData')
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex gap-3`}>
            <button
              onClick={handlePrint}
              className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2`}
            >
              <Printer size={20} />
              <span>{t('printReport')}</span>
            </button>
            
            <button
              onClick={handleDownload}
              className={`flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2`}
            >
              <Download size={20} />
              <span>{t('downloadPDF')}</span>
            </button>
            
            <button
              onClick={onClose}
              className={`flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2`}
            >
              <X size={20} />
              <span>{t('close')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintSummaryModal;